<!-- Chat list row showing avatar, name, timestamp, and message snippet preview. -->

<script lang="ts" setup>
import { getFullName } from '@sigma/utils/getFullName.js';

import Avatar from '../Avatar.vue';
import ChatAgentAvatar from './ChatAgentAvatar.vue';

export type ChatListItemType = 'agent' | 'person';

interface Props {
  type: ChatListItemType;
  firstName?: string | null;
  lastName?: string | null;
  nameFallback?: string;
  snippet: string;
  /** Render the snippet in italics — used for descriptor snippets like "Attached files". */
  snippetItalic?: boolean;
  time: string;
  title?: string;
  unread?: boolean;
  avatarUrl?: string;
}

defineProps<Props>();
</script>

<template>
  <div :class="css.item">
    <ChatAgentAvatar v-if="type === 'agent'" />
    <Avatar
      v-else
      :first-name="firstName"
      :last-name="lastName"
      :photo="avatarUrl"
      color="blue"
    />

    <div :class="css.content">
      <div :class="css.meta">
        <span :class="[css.name, unread && css.nameUnread]">
          {{ getFullName(firstName, lastName) || nameFallback }}
        </span>
        <span
          v-if="unread"
          :class="css.timeUnread"
        >
          <span :class="css.unreadDot" />
          {{ time }}
        </span>
        <span
          v-else
          :class="css.time"
        >
          {{ time }}
        </span>
      </div>
      <span
        v-if="title"
        :class="[css.title, unread && css.titleUnread]"
      >
        {{ title }}
      </span>
      <span :class="[css.snippet, unread && css.snippetUnread, snippetItalic && css.snippetItalic]">
        {{ snippet }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" module="css">
.item {
  display: flex;
  gap: $spacing-12;
  --avatar-size: 44px;
}

.agentAvatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: $border-radius-full;
  background: $color-doctor-600;
  flex-shrink: 0;
}

.agentLogo {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.content {
  display: flex;
  flex-direction: column;
  gap: $spacing-04;
  min-width: 0;
  flex: 1;
}

.meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.name {
  font-size: $font-size-13;
  font-weight: $font-weight-600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.nameUnread {
  font-weight: $font-weight-700;
}

.time {
  font-size: $font-size-12;
  color: $color-gray-600;
  flex-shrink: 0;
}

.timeUnread {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: $font-size-12;
  font-weight: $font-weight-600;
  color: $color-doctor-600;
  flex-shrink: 0;
}

.unreadDot {
  width: 7px;
  height: 7px;
  border-radius: $border-radius-full;
  background: $color-doctor-600;
}

.title {
  font-size: $font-size-14;
  font-weight: $font-weight-600;
}

.titleUnread {
  font-weight: $font-weight-700;
}

.snippet {
  font-size: $font-size-13;
  color: $color-gray-600;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.snippetUnread {
  color: $color-gray-900;
  font-weight: $font-weight-600;
}

.snippetItalic {
  font-style: italic;
}
</style>
